import { CAMPAIGN_CARD_DIAL_SIZE } from "@/components/assemblies/campaignCard/constants";
import { RubyBudgetPlanPolicy, type RubyCampaignId } from "@/services/backend/RubyData";
import { aggregateAsyncData } from "@/utilities/requests";
import moment from "moment";
import { currencyFormatter } from "../../../../../utilities/currency";
import { languageString } from "../../../../../utilities/text";
import { useCampaignDataLoader } from "../../../../assemblies/campaignDataLoader/CampaignDataLoader";
import RequestLoader from "../../../../blocks/requestLoader/RequestLoader";
import StatRadial from "../../../../blocks/stats/radial/StatRadial";

interface BudgetRemainingDialProps {
    campaignId?: RubyCampaignId;
    size?: number;
}

export default function BudgetRemainingDial({ size = CAMPAIGN_CARD_DIAL_SIZE, campaignId }: BudgetRemainingDialProps) {
    const loader = useCampaignDataLoader(campaignId);
    const currency = loader.useCurrency();
    const budgetPacing = loader.useBudgetPacing();

    const formatCurrency = currencyFormatter(currency);
    const budget = loader.useActiveBudget();
    const channel = loader.useChannel();
    const today = moment.utc();
    const todayMetrics = loader.useMetricsSummary(
        [today.clone().startOf("day").valueOf(), today.clone().endOf("day").valueOf()],
        true
    );
    const isDaily = budget.data?.policy === RubyBudgetPlanPolicy.REPEATING || channel.data?.useLifetimeBudget === false;

    if (isDaily) {
        const dailyBudget = budget.data?.totalBudget;
        const dailyDial = (available: boolean) => (
            <DailyBudgetSpendStat
                label={languageString("campaign.budget.dailySpend", "Spent today")}
                hint={languageString(
                    "campaign.budget.dailySpendHint",
                    "Today's reported spend divided by the configured daily budget. The day runs from 00:00 to 23:59 UTC. Reporting may be delayed."
                )}
                value={available ? (todayMetrics.data?.spend ?? NaN) : NaN}
                max={available ? (Number.isFinite(dailyBudget) ? dailyBudget : NaN) : NaN}
                formatter={formatCurrency}
                subLabel={languageString("campaign.budget.dailyBudgetLabel", "Daily budget: {1}", [
                    Number.isFinite(dailyBudget) && dailyBudget >= 0 ? formatCurrency(dailyBudget) : "\u2014",
                ])}
                size={size}
            />
        );
        return (
            <div className="u-relative">
                <RequestLoader
                    request={aggregateAsyncData(budget, todayMetrics)}
                    noPending
                    placeholder={dailyDial(false)}
                    fallback={<div className="u-fade2">{dailyDial(false)}</div>}
                >
                    {dailyDial(true)}
                </RequestLoader>
            </div>
        );
    }

    return (
        <div className="u-relative">
            <RequestLoader
                request={budgetPacing}
                noPending
                placeholder={
                    <StatRadial
                        label={languageString("campaign.listing.budgetRemaining")}
                        value={0}
                        max={1000}
                        formatter={formatCurrency}
                        subLabel={languageString("campaign.budget.daysRemain", "", [0])}
                        size={size}
                    />
                }
                fallback={
                    <div className="u-fade2">
                        <StatRadial
                            label={languageString("campaign.listing.budgetRemaining")}
                            value={0}
                            max={1000}
                            formatter={formatCurrency}
                            subLabel={languageString("campaign.budget.daysRemain", "", [0])}
                            size={size}
                        />
                    </div>
                }
            >
                <StatRadial
                    label={languageString("campaign.listing.budgetRemaining")}
                    value={budgetPacing.data?.unspentBudget}
                    max={budgetPacing.data?.budget?.totalBudget ?? 0}
                    formatter={formatCurrency}
                    subLabel={languageString("campaign.budget.daysRemain", "", [
                        budgetPacing.data?.budget?.daysRemaining,
                    ])}
                    size={size}
                />
            </RequestLoader>
        </div>
    );
}

// A missing/zero daily budget has no meaningful percentage, but reported spend can still be shown.
export class DailyBudgetSpendStat extends StatRadial {
    getPercent(value = this.props.value, min = 0, max = this.props.max) {
        return Number.isFinite(value) && Number.isFinite(max) && max > 0 ? value / max : 0;
    }

    formatPercent(value: number) {
        return Number.isFinite(this.props.value) && Number.isFinite(this.props.max) && this.props.max > 0
            ? super.formatPercent(value)
            : "\u2014";
    }

    formatValue(value: number) {
        return Number.isFinite(value) ? super.formatValue(value) : "\u2014";
    }
}
